// frontend/admin/exams/index.js
// Updated version with correct initialization order

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";
import { useFormatDate } from "hooks/useFormatDate";
import { DateTimeCell } from "components/DateCells";

import {
  getStudentExams,
  getAnnotators,
  assignStudentExams,
  getAdmins,
} from "services/sampleService";
import { getUserInfo } from "services/authService";
import { EXAM_STATUS_FILTER_OPTIONS, EXAM_STATUS_LABEL_KEYS } from "constants/examStatus";
import ReassignAnnotatorModal from "layouts/admin/exams/modals/ReassignAnnotatorModal";

const getStatusOptions = (t) =>
  EXAM_STATUS_FILTER_OPTIONS.map(({ value, labelKey }) => ({
    value,
    label: labelKey ? t(labelKey, value) : t("exams.filters.any", "Any"),
  }));

/** Map English status from API to display label (localized UI). */
function translateExamStatusLabel(status, t) {
  if (!status) return "";
  const key = EXAM_STATUS_LABEL_KEYS[status];
  return key ? t(key, status) : status;
}

function isSlotReassignable(annotator) {
  if (!annotator?.status) return true;
  return annotator.status === "Pending" || annotator.status === "In Progress";
}

function canReassignExam(row) {
  return isSlotReassignable(row?.annotator1) || isSlotReassignable(row?.annotator2);
}

function resolveAssignErrorMessage(apiError, t) {
  const byCode = {
    SAME_ANNOTATOR: t(
      "exams.notifications.sameAnnotator",
      "Annotator 1 and Annotator 2 cannot be the same."
    ),
    NO_ANNOTATOR: t("exams.notifications.noAnnotator", "Provide at least one annotator."),
    ANNOTATOR_ALREADY_ASSIGNED: t(
      "exams.notifications.annotatorAlreadyAssigned",
      "This exam is already assigned to an annotator."
    ),
  };
  return (
    byCode[apiError?.code] ??
    apiError?.message ??
    t("exams.notifications.assignmentFailedMessage", "Failed to assign annotators")
  );
}

/* ----------------------- */
/*   Helper UI Components  */
/* ----------------------- */

function SelectAllHeader({ rowsData, selectedIds, setSelectedIds, t }) {
  const allIds = rowsData.map((r) => r._row_id);

  // Only check IDs that exist in current data
  const currentSelectedIds = selectedIds.filter((id) => allIds.includes(id));

  const checked = rowsData.length > 0 && currentSelectedIds.length === rowsData.length;
  const indeterminate =
    currentSelectedIds.length > 0 && currentSelectedIds.length < rowsData.length;

  const onChange = (e) => {
    if (e.target.checked) {
      // Add all current IDs to selection
      const newSelectedIds = [...new Set([...selectedIds, ...allIds])];
      setSelectedIds(newSelectedIds);
    } else {
      // Remove only current IDs from selection
      const newSelectedIds = selectedIds.filter((id) => !allIds.includes(id));
      setSelectedIds(newSelectedIds);
    }
  };

  return (
    <Checkbox
      indeterminate={indeterminate}
      checked={checked}
      onChange={onChange}
      inputProps={{ "aria-label": t ? t("exams.table.selectAll", "Select All") : "Select All" }}
    />
  );
}

SelectAllHeader.propTypes = {
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      _row_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    .isRequired,
  setSelectedIds: PropTypes.func.isRequired,
  t: PropTypes.func,
};

SelectAllHeader.defaultProps = {
  t: null,
};

function RowSelectCell({ row, selectedIds, setSelectedIds }) {
  const rowId = row?.original?._row_id;
  const isChecked = selectedIds.includes(rowId);

  const onChange = (e) => {
    setSelectedIds((prev) =>
      e.target.checked ? [...prev, rowId] : prev.filter((x) => x !== rowId)
    );
  };

  return <Checkbox checked={isChecked} onChange={onChange} />;
}

RowSelectCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      _row_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    .isRequired,
  setSelectedIds: PropTypes.func.isRequired,
};

// Helper component to display Annotator details
function AnnotatorDetails({ annotator, type }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatDate();

  if (!annotator || !annotator.id || annotator.id === 0) {
    return (
      <MDTypography variant="caption" color="text.secondary">
        {t("exams.table.notAssigned", "Not Assigned")}
      </MDTypography>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "In Progress":
        return "info";
      case "Submitted":
        return "success";
      case "Annotated Once":
        return "primary";
      case "Annotated Twice":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">
            <strong>{t("exams.table.assignedTo", "Assigned To")}:</strong>{" "}
            {annotator.name || `ID: ${annotator.id}`}
          </Typography>
          <Typography variant="body2">
            <strong>{t("exams.table.assignedOn", "Assigned On")}:</strong>{" "}
            {annotator.assigned_on
              ? formatDateTime(annotator.assigned_on)
              : t("exams.table.notAvailable", "N/A")}
          </Typography>
          <Typography variant="body2">
            <strong>{t("exams.table.assignedBy", "Assigned By")}:</strong>{" "}
            {annotator.assigned_by_name || `ID: ${annotator.assigned_by}`}
          </Typography>
          <Typography variant="body2">
            <strong>{t("exams.table.status", "Status")}:</strong>{" "}
            <Chip
              label={translateExamStatusLabel(annotator.status, t)}
              size="small"
              color={getStatusColor(annotator.status)}
              sx={{ height: 20 }}
            />
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: type === "A1" ? "primary.main" : "secondary.main",
            fontSize: "0.875rem",
          }}
        >
          {annotator.name?.charAt(0) || type}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {annotator.name || `Annotator ${annotator.id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {translateExamStatusLabel(annotator.status, t)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

AnnotatorDetails.propTypes = {
  annotator: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    assigned_on: PropTypes.string,
    assigned_by: PropTypes.number,
    assigned_by_name: PropTypes.string,
    status: PropTypes.string,
  }),
  type: PropTypes.oneOf(["A1", "A2"]).isRequired,
};

AnnotatorDetails.defaultProps = {
  annotator: null,
};

// Custom cell components with PropTypes
function Annotator1DetailsCell({ row }) {
  return <AnnotatorDetails annotator={row.original.annotator1} type="A1" />;
}

Annotator1DetailsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      annotator1: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        assigned_on: PropTypes.string,
        assigned_by: PropTypes.number,
        assigned_by_name: PropTypes.string,
        status: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
};

function Annotator2DetailsCell({ row }) {
  return <AnnotatorDetails annotator={row.original.annotator2} type="A2" />;
}

Annotator2DetailsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      annotator2: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        assigned_on: PropTypes.string,
        assigned_by: PropTypes.number,
        assigned_by_name: PropTypes.string,
        status: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
};

function OverallStatusCell({ value }) {
  const { t } = useTranslation();
  const getColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "In Progress":
        return "info";
      case "Annotated Once":
        return "primary";
      case "Annotated Twice":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={translateExamStatusLabel(value, t)}
      color={getColor(value)}
      sx={{ fontWeight: 500 }}
    />
  );
}

OverallStatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

function ActionsCell({ row, onReassign, t }) {
  const navigate = useNavigate();
  const showViewSample = row.original.overall_status === "Annotated Once";
  const showReassign = canReassignExam(row.original);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} flexWrap="wrap">
      {showReassign ? (
        <MDButton
          size="small"
          color="secondary"
          variant="outlined"
          startIcon={<EditIcon fontSize="small" />}
          onClick={() => onReassign(row.original)}
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("exams.reassign.editAssignment", "Edit assignment")}
        </MDButton>
      ) : null}
      {showViewSample ? (
        <MDButton
          size="small"
          color="info"
          variant="outlined"
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={() =>
            navigate(`/admin/exams/view-sample?exam_id=${encodeURIComponent(row.original.exam_id)}`)
          }
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("exams.buttons.viewSample", "View Sample")}
        </MDButton>
      ) : null}
    </MDBox>
  );
}

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      overall_status: PropTypes.string,
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      annotator1: PropTypes.object,
      annotator2: PropTypes.object,
    }).isRequired,
  }).isRequired,
  onReassign: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/* ----------------------- */
/*        Main Page        */
/* ----------------------- */

function ExamsIndex() {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);

  // Initialize filters with user info if available immediately
  const initialUser = getUserInfo();
  const [filters, setFilters] = useState({
    status: "Pending",
    exam_id: "",
    annotator1_id: "",
    annotator2_id: "",
    pending_assignment: false,
    page: 1,
    limit: 25,
  });

  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [total, setTotal] = useState(0);

  // selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [annotators, setAnnotators] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [assignA1, setAssignA1] = useState("");
  const [assignA2, setAssignA2] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignExamRow, setReassignExamRow] = useState(null);

  // snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    title: "",
    message: "",
  });

  const showSnackbar = ({ color = "success", title = "", message = "", duration = 4000 }) => {
    setSnackbar({ open: true, color, title, message });

    setTimeout(() => {
      setSnackbar((s) => ({ ...s, open: false }));
    }, duration);
  };

  const closeSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const statusOptions = getStatusOptions(t);

  // Get current user info
  useEffect(() => {
    const user = getUserInfo();
    setUserInfo(user);
  }, []);

  const openReassignModal = (row) => {
    setReassignExamRow(row);
    setReassignOpen(true);
  };

  const closeReassignModal = () => {
    setReassignOpen(false);
    setReassignExamRow(null);
  };

  const handleReassignSuccess = async (notification) => {
    if (notification) {
      showSnackbar(notification);
    }
    await fetchExams();
  };

  const handleReassignError = (notification) => {
    if (notification) {
      showSnackbar(notification);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <SelectAllHeader
            rowsData={rowsData}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            t={t}
          />
        ),
        accessor: "_select",
        align: "center",
        Cell: (cellProps) => (
          <RowSelectCell
            row={cellProps.row}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        ),
        width: "5%",
      },
      {
        Header: t("exams.table.examId", "Session ID"),
        accessor: "exam_id",
        align: "left",
      },
      {
        Header: t("exams.table.examDate", "Session Date"),
        accessor: "start_time",
        align: "left",
        Cell: DateTimeCell,
      },
      {
        Header: t("exams.table.annotator1Details", "Annotator 1 Details"),
        accessor: "annotator1_details",
        align: "left",
        Cell: Annotator1DetailsCell,
      },
      {
        Header: t("exams.table.annotator2Details", "Annotator 2 Details"),
        accessor: "annotator2_details",
        align: "left",
        Cell: Annotator2DetailsCell,
      },
      {
        Header: t("exams.table.overallStatus", "Overall Status"),
        accessor: "overall_status",
        align: "left",
        Cell: OverallStatusCell,
      },
      {
        Header: t("exams.table.actions", "Actions"),
        accessor: "actions",
        align: "center",
        Cell: (cellProps) => (
          <ActionsCell row={cellProps.row} onReassign={openReassignModal} t={t} />
        ),
      },
    ],
    [rowsData, selectedIds, t]
  );

  const fetchExams = async (useFilters = filters) => {
    try {
      setLoading(true);

      // Prepare filters for API call
      const apiFilters = { ...useFilters };

      console.log("Sending filters to API:", apiFilters);
      //console.log("Admin filter value:", apiFilters.assigned_by_admin);

      const resp = await getStudentExams(apiFilters);

      const mapped = (resp.data || []).map((e) => {
        // Create annotator objects with all details
        const annotator1 = {
          id: e.annotator1_id,
          name: e.annotator1_name,
          assigned_on: e.annotator1_assigned_on,
          assigned_by: e.annotator1_assigned_by,
          assigned_by_name: e.annotator1_assigned_by_name || `ID: ${e.annotator1_assigned_by}`,
          status: e.annotator1_status,
        };

        const annotator2 = {
          id: e.annotator2_id,
          name: e.annotator2_name,
          assigned_on: e.annotator2_assigned_on,
          assigned_by: e.annotator2_assigned_by,
          assigned_by_name: e.annotator2_assigned_by_name || `ID: ${e.annotator2_assigned_by}`,
          status: e.annotator2_status,
        };

        return {
          _row_id: e.id ?? e.exam_id,
          exam_id: e.exam_id ?? "",
          start_time: e.start_time ?? "",
          annotator1,
          annotator2,
          // Add searchable string fields for annotator details
          annotator1_details: annotator1.name || `ID: ${annotator1.id}` || "Not Assigned",
          annotator2_details: annotator2.name || `ID: ${annotator2.id}` || "Not Assigned",
          overall_status: e.status ?? "Pending",
          originalData: e,
        };
      });

      setRowsData(mapped);
      setTotal(resp.total_records ?? mapped.length);
      setSelectedIds([]); // Clear selections when data changes
    } catch (err) {
      console.error("fetchExams error:", err);
      setRowsData([]);
      setTotal(0);
      setSelectedIds([]);
      showSnackbar({
        color: "error",
        title: t("exams.notifications.fetchError", "Fetch Error"),
        message: t("exams.notifications.fetchErrorMessage", "Failed to load exams data"),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnotators = async () => {
    try {
      // Active annotators only (assignment + filters); admins see org-wide annotators
      const response = await getAnnotators();
      setAnnotators(response || []);
    } catch (err) {
      console.error("fetchAnnotators error:", err);
      setAnnotators([]);
      showSnackbar({
        color: "error",
        title: t("exams.notifications.fetchError", "Fetch Error"),
        message: t("exams.notifications.annotatorsFetchError", "Failed to load annotators"),
      });
    }
  };

  function formatAssignmentIssues(errors = {}) {
    const issueMap = {
      annotator1_already_assigned: t(
        "exams.notifications.annotator1AlreadyAssigned",
        "Annotator 1 already assigned"
      ),
      annotator2_already_assigned: t(
        "exams.notifications.annotator2AlreadyAssigned",
        "Annotator 2 already assigned"
      ),
    };

    return Object.entries(errors)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => {
        const label = issueMap[key] || key;
        const unit =
          count > 1
            ? t("exams.notifications.assignmentIssueExams", "exams")
            : t("exams.notifications.assignmentIssueExam", "exam");
        return `• ${label}: ${count} ${unit}`;
      })
      .join("\n");
  }

  useEffect(() => {
    const init = async () => {
      await fetchAnnotators();
      // Pass the current filters to fetchExams to ensure admin filter is applied
      await fetchExams(filters);
    };
    init();
  }, []);

  // Re-fetch exams when filters change (except initial load)
  useEffect(() => {
    if (userInfo) {
      // Only refetch if filters have changed (not on initial userInfo set)
      fetchExams();
    }
  }, [
    userInfo,
    filters.status,
    filters.exam_id,
    filters.annotator1_id,
    filters.annotator2_id,
    //filters.assigned_by_admin,
    filters.pending_assignment,
    filters.page,
  ]);

  const onApply = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    fetchExams(next);
  };

  const onReset = () => {
    // When resetting, check if user is admin and restore their filter
    const reset = {
      status: "Pending",
      exam_id: "",
      annotator1_id: "",
      annotator2_id: "",
      //assigned_by_admin: userInfo?.role === "Admin" && userInfo?.id ? userInfo.id : "",
      pending_assignment: false,
      page: 1,
      limit: 25,
    };
    setFilters(reset);
    fetchExams(reset);
  };

  const onPageChange = (newPage) => {
    const next = { ...filters, page: newPage };
    setFilters(next);
    fetchExams(next);
  };

  const onAssign = async () => {
    if (selectedIds.length === 0) {
      showSnackbar({
        color: "error",
        title: t("exams.notifications.noSelection", "No Selection"),
        message: t("exams.notifications.noSelectionMessage", "Please select exams to assign"),
      });
      return;
    }

    // Filter selectedIds to only include those that exist in current rowsData
    const currentRowIds = rowsData.map((r) => r._row_id);
    const validSelectedIds = selectedIds.filter((id) => currentRowIds.includes(id));

    if (validSelectedIds.length === 0) {
      showSnackbar({
        color: "error",
        title: t("exams.notifications.invalidSelection", "Invalid Selection"),
        message: t(
          "exams.notifications.invalidSelectionMessage",
          "Selected exams are no longer in the current view"
        ),
      });
      setSelectedIds([]);
      return;
    }

    const exam_ids = validSelectedIds;

    if (assignA1 && assignA2 && String(assignA1) === String(assignA2)) {
      showSnackbar({
        color: "error",
        title: t("exams.notifications.assignmentFailed", "Assignment Failed"),
        message: t(
          "exams.notifications.sameAnnotator",
          "Annotator 1 and Annotator 2 cannot be the same."
        ),
      });
      return;
    }

    const payload = {
      exam_ids,
      annotator1_id: assignA1 || undefined,
      annotator2_id: assignA2 || undefined,
      assigned_by: userInfo?.id || undefined,
    };

    try {
      setLoading(true);

      const resp = await assignStudentExams(payload);

      const isWarning = resp?.status === "warning";

      const issueDetails =
        isWarning && resp?.errors ? "\n\n" + formatAssignmentIssues(resp.errors) : "";

      const assignedCount = Number(resp?.assigned ?? 0);

      showSnackbar({
        color: isWarning ? "warning" : "success",
        title: isWarning
          ? t("exams.notifications.assignmentIssues", "Assignment Issues Found")
          : t("exams.notifications.assignmentSuccess", "Assignment Successful"),
        message: isWarning
          ? `${resp?.message || ""}${issueDetails}`
          : t(
              "exams.notifications.assignmentSuccessDetail",
              "Assignment completed successfully — {{count}} exam(s) updated."
            ).replace("{{count}}", String(assignedCount)),
      });

      if (resp?.assigned > 0) {
        setAssignA1("");
        setAssignA2("");
        await fetchExams();
      }
    } catch (err) {
      console.error("Assign error:", err);

      // ✅ FIXED
      const apiError = err;

      showSnackbar({
        color: "error",
        title: t("exams.notifications.assignmentFailed", "Assignment Failed"),
        message: resolveAssignErrorMessage(apiError, t),
      });
    } finally {
      setLoading(false);
    }
  };

  const assignmentBarVisible = selectedIds.length > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Filters */}
            <Card sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", boxShadow: 2 }}>
              <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                {/* Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="status-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.status", "Status")}
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                    label={t("exams.filters.status", "Status")}
                    displayEmpty
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "14px",
                      },
                    }}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value || "any"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Exam ID field */}
                <TextField
                  label={t("exams.filters.sessionId", "Session ID")}
                  size="small"
                  value={filters.exam_id}
                  onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      onApply();
                    }
                  }}
                  sx={{
                    minWidth: 250,
                    height: "56px",
                  }}
                  InputProps={{
                    sx: {
                      height: "56px",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      backgroundColor: "background.paper",
                      px: 1,
                    },
                  }}
                />

                {/* Annotator 1 Dropdown - Single Select */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="annotator1-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.annotator1", "Annotator 1")}
                  </InputLabel>

                  <Select
                    labelId="annotator1-label"
                    value={filters.annotator1_id}
                    displayEmpty
                    onChange={(e) =>
                      setFilters({ ...filters, annotator1_id: e.target.value, page: 1 })
                    }
                    label={t("exams.filters.annotator1", "Annotator 1")}
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("exams.filters.all", "All")}</em>
                    </MenuItem>

                    {/* Active annotators only (excludes deleted accounts) */}
                    {annotators.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Annotator 2 Dropdown - Single Select */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="annotator2-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.annotator2", "Annotator 2")}
                  </InputLabel>

                  <Select
                    labelId="annotator2-label"
                    value={filters.annotator2_id}
                    displayEmpty
                    onChange={(e) =>
                      setFilters({ ...filters, annotator2_id: e.target.value, page: 1 })
                    }
                    label={t("exams.filters.annotator2", "Annotator 2")}
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("exams.filters.all", "All")}</em>
                    </MenuItem>

                    {/* ✅ All annotators including deleted (for both SuperAdmin and Admin) */}
                    {annotators.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Apply and Reset buttons */}
                <MDButton color="secondary" variant="outlined" onClick={onReset} disabled={loading}>
                  {t("exams.buttons.reset", "Reset")}
                </MDButton>
              </MDBox>
            </Card>

            {/* Assignment Bar */}
            {assignmentBarVisible && (
              <Card sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", boxShadow: 1 }}>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <MDTypography variant="button">
                    {t("exams.assignment.selectedCount", "Selected:")}{" "}
                    <strong>{selectedIds.length}</strong>
                  </MDTypography>

                  <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel
                        id="assign-a1-label"
                        shrink={true}
                        sx={{
                          backgroundColor: "background.paper",
                          px: 1,
                          transform: "translate(14px, -9px) scale(0.75)",
                        }}
                      >
                        {t("exams.assignment.annotator1", "Annotator 1")}
                      </InputLabel>
                      <Select
                        labelId="assign-a1-label"
                        value={assignA1}
                        onChange={(e) => setAssignA1(e.target.value)}
                        label={t("exams.assignment.annotator1", "Annotator 1")}
                        sx={{
                          "& .MuiSelect-select": {
                            paddingTop: "14px",
                            paddingBottom: "8px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("exams.table.none", "None")}</em>
                        </MenuItem>
                        {annotators.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel
                        id="assign-a2-label"
                        shrink={true}
                        sx={{
                          backgroundColor: "background.paper",
                          px: 1,
                          transform: "translate(14px, -9px) scale(0.75)",
                        }}
                      >
                        {t("exams.assignment.annotator2", "Annotator 2")}
                      </InputLabel>
                      <Select
                        labelId="assign-a2-label"
                        value={assignA2}
                        onChange={(e) => setAssignA2(e.target.value)}
                        label={t("exams.assignment.annotator2", "Annotator 2")}
                        sx={{
                          "& .MuiSelect-select": {
                            paddingTop: "14px",
                            paddingBottom: "8px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("exams.table.none", "None")}</em>
                        </MenuItem>
                        {annotators.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <MDButton
                      color="info"
                      onClick={onAssign}
                      disabled={loading || (assignA1 === "" && assignA2 === "")}
                    >
                      {loading
                        ? t("exams.buttons.assigning", "Assigning...")
                        : t("exams.buttons.assign", "Assign")}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}

            {/* Table - Fixed entries per page to 25 */}
            <Card
              sx={{
                boxShadow: 4,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#f9fafe",
                border: "1px solid #e0e0e0",
              }}
            >
              <MDBox>
                <DataTable
                  table={{ columns, rows: rowsData }}
                  isSorted
                  canSearch
                  showTotalEntries
                  totalRecords={total}
                  entriesPerPage={{
                    defaultValue: 25,
                    entries: [25, 50, 100, 250],
                    onChange: (value) => {
                      const next = { ...filters, limit: value, page: 1 };
                      setFilters(next);
                      fetchExams(next);
                    },
                  }}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: filters.page - 1,
                    onPageChange: (_, newPage) => onPageChange(newPage + 1),
                  }}
                  noEndBorder={false}
                />
              </MDBox>
            </Card>

            <MDBox mt={1} textAlign="right" color="text">
              <MDTypography variant="caption">
                {t("exams.table.totalRecords", "Total Records")}: {total}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={closeSnackbar}
      >
        <Alert
          icon={false}
          elevation={6}
          variant="filled"
          severity={snackbar.color === "error" ? "error" : "success"}
          sx={{
            width: "100%",
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
            p: 0,
            overflow: "hidden",
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              onClick={closeSnackbar}
              sx={{ color: "rgba(255,255,255,0.95)", mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              background:
                snackbar.color === "error"
                  ? (theme) => theme.palette.error.main
                  : (theme) => theme.palette.success.main,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
              {snackbar.title || (snackbar.color === "error" ? "Error" : "Success")}
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 1.25, background: "transparent" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.95)" }}>
              {snackbar.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      <ReassignAnnotatorModal
        open={reassignOpen}
        onClose={closeReassignModal}
        exam={reassignExamRow}
        annotators={annotators}
        userInfo={userInfo}
        onSuccess={handleReassignSuccess}
        onError={handleReassignError}
      />
    </DashboardLayout>
  );
}

export default ExamsIndex;
